import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  Alert,
} from '@mui/material';
import {
  PersonAdd,
  Link,
  HourglassEmpty,
  Close,
} from '@mui/icons-material';
import ChildRegistrationForm from './ChildRegistrationForm';
import ChildLinkingForm from './ChildLinkingForm';
import ChildPendingLinksForm from './ChildPendingLinksForm';
import { childManagementService, type ChildRegistrationRequest } from '../services/childManagementService';

interface ChildManagementDialogProps {
  open: boolean;
  onClose: () => void;
  onChildRegistered: (childData: any) => void;
  onChildLinked: (childId: number) => void;
  title?: string;
  parentId?: number;
  initialTab?: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`child-management-tabpanel-${index}`}
      aria-labelledby={`child-management-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ChildManagementDialog: React.FC<ChildManagementDialogProps> = ({
  open,
  onClose,
  onChildRegistered,
  onChildLinked,
  title = 'Manage Children',
  parentId,
  initialTab = 0,
}) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setActiveTab(initialTab);
    }
  }, [open, initialTab]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setError(null);
  };

  const handleChildRegister = async (childData: ChildRegistrationRequest) => {
    setLoading(true);
    setError(null);

    try {
      const result = await childManagementService.registerChild({
        ...childData,
        parentUserId: parentId || 0,
      });

      onChildRegistered(result);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to register child');
    } finally {
      setLoading(false);
    }
  };

  const handleChildLinked = async (childId: number) => {
    onChildLinked(childId);
    onClose();
  };

  const handleClose = () => {
    setActiveTab(0);
    setError(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '600px' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">{title}</Typography>
        <Button onClick={handleClose} size="small" sx={{ minWidth: 'auto', p: 1 }}>
          <Close />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="child management tabs"
            variant="fullWidth"
          >
            <Tab icon={<PersonAdd />} label="Register New" iconPosition="start" sx={{ minHeight: 48 }} />
            <Tab icon={<Link />} label="Add Registered" iconPosition="start" sx={{ minHeight: 48 }} />
            <Tab icon={<HourglassEmpty />} label="Pending Links" iconPosition="start" sx={{ minHeight: 48 }} />
          </Tabs>
        </Box>

        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        <TabPanel value={activeTab} index={0}>
          <ChildRegistrationForm
            onRegister={handleChildRegister}
            title="Register New Child"
            submitButtonText="Register Child"
            showStepper={true}
            parentId={parentId}
            mode="register"
          />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <ChildLinkingForm onLinkChild={handleChildLinked} title="Add Registered Child" />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <ChildPendingLinksForm onLinkCompleted={() => onChildLinked(0)} />
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={handleClose} disabled={loading}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChildManagementDialog;
